import { Box } from "@mui/material";
import { useState, useEffect, useMemo, useCallback } from "react";
import NetworkGraph from "./components/NetworkGraph";
import API from "../../../../api";
import AnomalyList from "./components/AnomalyList";
import AnomalyDetailDrawer from "./components/AnomalyDetailDrawer";
import BehaviorsFiltersBar from "./components/BehaviorsFiltersBar";
import { QUICK_PORTS, QUICK_FILTERS } from "./constants";

const DEFAULT_HOURS = "24";
// Prefiltro iniziale sullo score: sotto questa soglia le anomalie sono rumore (come nelle viste Security).
const DEFAULT_MIN_SCORE = 0.5;
const ALLOWED_HOURS = ["24", "48", "72"];

const PORT_TO_PROTOCOL = {
  0: "ICMP",
  20: "FTP",
  21: "FTP",
  22: "SSH",
  23: "TELNET",
  25: "SMTP",
  53: "DNS",
  69: "TFTP",
  80: "HTTP",
  88: "Kerberos",
  110: "POP3",
  111: "RPCbind/Portmapper",
  119: "NNTP",
  123: "NTP",
  135: "Microsoft RPC",
  139: "NetBIOS Session",
  143: "IMAP",
  161: "SNMP",
  162: "SNMP",
  389: "LDAP",
  443: "HTTPS / TLS",
  445: "SMB",
  464: "Kerberos",
  465: "SMTPS",
  500: "ISAKMP / IKE",
  514: "Syslog",
  587: "SMTP Submission",
  631: "IPP",
  636: "LDAPS",
  993: "IMAPS",
  995: "POP3S",
  1433: "Microsoft SQL Server",
  1521: "Oracle DB",
  1723: "PPTP VPN",
  1883: "MQTT",
  2049: "NFS",
  2375: "Docker API",
  2376: "Docker API",
  3306: "MySQL/MariaDB",
  3389: "RDP",
  5357: "WSDAPI",
  5432: "PostgreSQL",
  5900: "VNC",
  6379: "Redis",
  7680: "WUDO",
  8080: "HTTP/HTTPS alternative ports",
  8443: "HTTP/HTTPS alternative ports",
  27017: "MongoDB",
};

const getInitialUrlFilters = () => {
  const searchParams = new URLSearchParams(window.location.search);

  const hoursParam = searchParams.get("hours");
  const anomalyTypesParam = searchParams.get("anomaly_types");
  const peakScoreParam = searchParams.get("peak_score");
  const ipsParam = searchParams.get("ips");
  const severityParam = searchParams.get("severity");
  const quickPortsParam = searchParams.get("quick_ports");

  const initialHours = ALLOWED_HOURS.includes(hoursParam)
    ? hoursParam
    : DEFAULT_HOURS;

  const initialAnomalyTypes = anomalyTypesParam
    ? [
        ...new Set(
          anomalyTypesParam
            .split(",")
            .map((type) => type.trim())
            .filter(Boolean),
        ),
      ]
    : [];

  const parsedPeakScore = Number(peakScoreParam);

  const initialMinScore =
    peakScoreParam !== null &&
    Number.isFinite(parsedPeakScore) &&
    parsedPeakScore >= 0
      ? parsedPeakScore
      : DEFAULT_MIN_SCORE;

  const parseList = (value) =>
    value ? [...new Set(value.split(",").map((v) => v.trim()).filter(Boolean))] : [];

  return {
    hours: initialHours,
    anomalyTypes: initialAnomalyTypes,
    minScore: initialMinScore,
    // Filtro Source IP (usato dal link "Open analysis" dei log Security).
    ips: parseList(ipsParam),
    // Senza parametro resta il default "high"; severity=all toglie il filtro.
    severity: severityParam === null ? ["high"] : severityParam === "all" ? [] : parseList(severityParam),
    // quick_ports=0 disattiva il filtro rapido sulle porte.
    quickPorts: quickPortsParam !== "0",
  };
};

const Behaviors = ({ selectedLecs }) => {
  const initialUrlFilters = useMemo(() => getInitialUrlFilters(), []);

  const [anomalies, setAnomalies] = useState([]);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);

  const [drawerAnomaly, setDrawerAnomaly] = useState(null);
  const [drawerSiblings, setDrawerSiblings] = useState([]);
  const [drawerIndex, setDrawerIndex] = useState(0);

  // Filtri server-side
  const [selectedIps, setSelectedIps] = useState(initialUrlFilters.ips);
  const [filterPorts, setFilterPorts] = useState(initialUrlFilters.quickPorts ? QUICK_PORTS : []);
  const [filterExcludedIps, setFilterExcludedIps] = useState([]);
  const [filterExcludedPorts, setFilterExcludedPorts] = useState([]);

  // Se presente nella query string utilizza hours=24/48/72.
  // In caso contrario mantiene il valore predefinito 24.
  const [hours, setHours] = useState(initialUrlFilters.hours);

  // Filtri client-side
  const [filterDstIps, setFilterDstIps] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const [filterSeverity, setFilterSeverity] = useState(initialUrlFilters.severity);

  // Se presente, anomaly_types viene trasformato in un array di valori univoci.
  const [filterTypes, setFilterTypes] = useState(
    initialUrlFilters.anomalyTypes,
  );

  // Se presente, peak_score imposta il valore iniziale del Min score.
  const [minScore, setMinScore] = useState(initialUrlFilters.minScore);

  const fetchAnomalies = useCallback(
    (
      ports,
      excludedIps,
      excludedPorts,
      ips,
      selectedHours,
      severity,
      types,
      dstIps,
      peakScoreMin,
    ) => {
      setAnomaliesLoading(true);

      const params = {
        hours: selectedHours,
      };

      if (ports.length > 0) {
        params.ports = ports.join(",");
      }

      if (excludedIps.length > 0) {
        params.exclude_ips = excludedIps.join(",");
      }

      if (excludedPorts.length > 0) {
        params.exclude_ports = excludedPorts.join(",");
      }

      if (ips.length > 0) {
        params.ips = ips.join(",");
      }

      // Filtri spostati lato server: prima venivano applicati solo dopo il
      // fetch di tutte le anomalie della finestra temporale, causando fetch
      // di decine di migliaia di documenti anche quando la UI ne mostrava
      // una frazione minima (es. default severity "high").
      if (severity?.length > 0) {
        params.severity = severity.join(",");
      }

      if (types?.length > 0) {
        params.anomaly_types = types.join(",");
      }

      if (dstIps?.length > 0) {
        params.dst_ips = dstIps.join(",");
      }

      if (peakScoreMin > 0) {
        params.min_score = peakScoreMin;
      }

      if (selectedLecs) {
        params.serials = JSON.stringify(selectedLecs);
      }

      API.getTiresiaAnomalies(params)
        .then((res) => {
          setAnomalies(res.data?.data ?? []);
        })
        .catch(() => {
          setAnomalies([]);
        })
        .finally(() => {
          setAnomaliesLoading(false);
        });
    },
    [selectedLecs],
  );

  useEffect(() => {
    fetchAnomalies(
      initialUrlFilters.quickPorts ? QUICK_PORTS : [],
      [],
      [],
      initialUrlFilters.ips,
      initialUrlFilters.hours,
      filterSeverity,
      initialUrlFilters.anomalyTypes,
      filterDstIps,
      initialUrlFilters.minScore,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAnomalies, initialUrlFilters.hours]);

  const handleApplyFilters = useCallback(() => {
    fetchAnomalies(
      filterPorts,
      filterExcludedIps,
      filterExcludedPorts,
      selectedIps,
      hours,
      filterSeverity,
      filterTypes,
      filterDstIps,
      minScore,
    );
  }, [
    fetchAnomalies,
    filterPorts,
    filterExcludedIps,
    filterExcludedPorts,
    selectedIps,
    hours,
    filterSeverity,
    filterTypes,
    filterDstIps,
    minScore,
  ]);

  const quickPortsActive =
    QUICK_PORTS.length === filterPorts.length &&
    QUICK_PORTS.every((port) => filterPorts.includes(port));

  const handleToggleQuickPorts = useCallback(() => {
    setFilterPorts((previousPorts) => {
      const isActive =
        QUICK_PORTS.length === previousPorts.length &&
        QUICK_PORTS.every((port) => previousPorts.includes(port));

      const nextPorts = isActive ? [] : [...QUICK_PORTS];

      fetchAnomalies(
        nextPorts,
        filterExcludedIps,
        filterExcludedPorts,
        selectedIps,
        hours,
        filterSeverity,
        filterTypes,
        filterDstIps,
        minScore,
      );

      return nextPorts;
    });
  }, [
    fetchAnomalies,
    filterExcludedIps,
    filterExcludedPorts,
    selectedIps,
    hours,
    filterSeverity,
    filterTypes,
    filterDstIps,
    minScore,
  ]);

  const filteredAnomalies = useMemo(
    () =>
      anomalies.filter((anomaly) => {
        if (
          filterSeverity.length > 0 &&
          !filterSeverity.includes(anomaly.severity_label)
        ) {
          return false;
        }

        if (
          filterTypes.length > 0 &&
          !filterTypes.some((type) =>
            anomaly.anomaly_types?.includes(type),
          )
        ) {
          return false;
        }

        if (
          minScore > 0 &&
          (anomaly.peak_score ?? 0) < minScore
        ) {
          return false;
        }

        if (filterDstIps.length > 0) {
          const destinationIps = (anomaly.graph?.edges ?? []).map(
            (edge) => edge.dst,
          );

          if (
            !filterDstIps.some((ip) =>
              destinationIps.includes(ip),
            )
          ) {
            return false;
          }
        }

        if (quickFilters.length > 0 && !quickFilters.every((key) => QUICK_FILTERS.find((q) => q.key === key)?.test(anomaly) ?? true)) {
          return false;
        }

        return true;
      }),
    [
      anomalies,
      quickFilters,
      filterSeverity,
      filterTypes,
      minScore,
      filterDstIps,
    ],
  );

  const openDrawerForHost = useCallback(
    (anomaly) => {
      const hostId = anomaly?.entity?.id;

      const siblings = hostId
        ? filteredAnomalies
            .filter(
              (currentAnomaly) =>
                currentAnomaly.entity?.id === hostId,
            )
            .sort(
              (firstAnomaly, secondAnomaly) =>
                (secondAnomaly.peak_score ?? 0) -
                (firstAnomaly.peak_score ?? 0),
            )
        : [anomaly];

      const startIndex = Math.max(
        0,
        siblings.findIndex(
          (currentAnomaly) =>
            currentAnomaly.anomaly_id === anomaly?.anomaly_id,
        ),
      );

      setDrawerSiblings(siblings);
      setDrawerIndex(startIndex);
      setDrawerAnomaly(siblings[startIndex] ?? anomaly);
    },
    [filteredAnomalies],
  );

  const handleDrawerNavigate = useCallback(
    (newIndex) => {
      setDrawerIndex(newIndex);
      setDrawerAnomaly(drawerSiblings[newIndex] ?? null);
    },
    [drawerSiblings],
  );

  const closeDrawer = useCallback(() => {
    setDrawerAnomaly(null);
    setDrawerSiblings([]);
    setDrawerIndex(0);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <BehaviorsFiltersBar
        anomalies={anomalies}
        selectedIps={selectedIps}
        setSelectedIps={setSelectedIps}
        filterPorts={filterPorts}
        setFilterPorts={setFilterPorts}
        filterExcludedIps={filterExcludedIps}
        setFilterExcludedIps={setFilterExcludedIps}
        filterExcludedPorts={filterExcludedPorts}
        setFilterExcludedPorts={setFilterExcludedPorts}
        hours={hours}
        setHours={setHours}
        onApplyFilters={handleApplyFilters}
        filterDstIps={filterDstIps}
        setFilterDstIps={setFilterDstIps}
        filterSeverity={filterSeverity}
        setFilterSeverity={setFilterSeverity}
        filterTypes={filterTypes}
        setFilterTypes={setFilterTypes}
        minScore={minScore}
        setMinScore={setMinScore}
        quickFilters={quickFilters}
        setQuickFilters={setQuickFilters}
        quickPortsActive={quickPortsActive}
        onToggleQuickPorts={handleToggleQuickPorts}
      />

      <NetworkGraph
        data={filteredAnomalies}
        severityFilter={filterSeverity}
        onOpenDrawer={openDrawerForHost}
        loading={anomaliesLoading}
      />

      <AnomalyList
        anomalies={filteredAnomalies}
        loading={anomaliesLoading}
        onOpenDrawer={openDrawerForHost}
        filterTypes={filterTypes}
      />

      <AnomalyDetailDrawer
        anomaly={drawerAnomaly}
        onClose={closeDrawer}
        PORT_TO_PROTOCOL={PORT_TO_PROTOCOL}
        filterTypes={filterTypes}
        siblings={drawerSiblings}
        activeIndex={drawerIndex}
        onNavigate={handleDrawerNavigate}
      />
    </Box>
  );
};

export default Behaviors;